import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import theme from './theme';
import { Layout } from './components/Layout';
import { Dashboard } from './pages/Dashboard';
import { Jobs } from './pages/Jobs';
import { JobDetails } from './pages/JobDetails';
import { Scheduling } from './pages/Scheduling';
import { Resources } from './pages/Resources';
import { Reporters } from './pages/Reporters';
import { ReporterDetail } from './pages/ReporterDetail';
import { Assigners } from './pages/Assigners';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { Login } from './auth/Login';
import { JOBS_ROLES, SCHEDULING_ROLES, SCHEDULING_ADMIN_ONLY, ALL_ADMIN_ROLES } from './auth/roles';
import './styles/dataGrid.css';

// Placeholder components for routes not yet implemented
const BackgroundTasks: React.FC = () => (
  <Layout pageTitle="Background Tasks">
    <Typography>Background task monitoring coming soon...</Typography>
  </Layout>
);

const Settings: React.FC = () => (
  <Layout pageTitle="Settings">
    <Typography>Admin settings coming soon...</Typography>
  </Layout>
);

// Route guard that checks role access
const RequireRole: React.FC<{ roles: string[]; children: React.ReactNode }> = ({ roles, children }) => {
  const { hasAnyRole } = useAuth();
  if (!hasAnyRole(roles)) {
    return <DefaultRedirect />;
  }
  return <>{children}</>;
};

// Redirects to the first accessible page based on the user's roles
const DefaultRedirect: React.FC = () => {
  const { hasAnyRole } = useAuth();
  if (hasAnyRole(JOBS_ROLES)) return <Navigate to="/" replace />;
  if (hasAnyRole(SCHEDULING_ROLES)) return <Navigate to="/scheduling" replace />;
  return <Navigate to="/resources" replace />;
};

// Protected routes component that must be inside AuthProvider
const ProtectedRoutes: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = window.location;

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    // Store the current path so we can redirect back after login
    const returnUrl = location.pathname + location.search;
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
  }

  return (
    <Routes>
      <Route path="/" element={<RequireRole roles={JOBS_ROLES}><Dashboard /></RequireRole>} />
      <Route path="/jobs" element={<RequireRole roles={JOBS_ROLES}><Jobs /></RequireRole>} />
      <Route path="/jobs/:jobNumber" element={<RequireRole roles={JOBS_ROLES}><JobDetails /></RequireRole>} />
      <Route path="/scheduling" element={<RequireRole roles={SCHEDULING_ROLES}><Scheduling /></RequireRole>} />
      <Route path="/resources" element={<RequireRole roles={ALL_ADMIN_ROLES}><Resources /></RequireRole>} />
      <Route path="/reporters" element={<RequireRole roles={SCHEDULING_ROLES}><Reporters /></RequireRole>} />
      <Route path="/reporters/:reporterId" element={<RequireRole roles={SCHEDULING_ROLES}><ReporterDetail /></RequireRole>} />
      <Route path="/assigners" element={<RequireRole roles={SCHEDULING_ADMIN_ONLY}><Assigners /></RequireRole>} />
      <Route path="/tasks" element={<RequireRole roles={JOBS_ROLES}><BackgroundTasks /></RequireRole>} />
      <Route path="/settings" element={<Settings />} />
      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <AuthProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/*" element={<ProtectedRoutes />} />
            </Routes>
          </Router>
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;